import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
import uniqueId from 'lodash/uniqueId';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { Droppable, type DroppableProvided } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import DisabledButtonWithTooltip from '@atlassian/jira-disabled-button-with-tooltip/src/index.tsx';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { NEW_FIELD_OPTION_PREFIX } from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import { OPTION_DRAGGABLE_TYPE } from '@atlassian/jira-project-settings-apps-common/src/options/index.tsx';
import { toOptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getRandomColor } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AssociatedField, OptionType } from '../../../../types/layout-items.tsx';
import type { Limits, ValidationError } from '../../../../types/validation.tsx';
import { POSITION_FIELD_OPTIONS } from '../../../../utils/validation/constants.tsx';
import messages from './messages.tsx';
import type { DefaultValueControlComponentProps, Props as OptionProps } from './option/view.tsx';

export type Props = {
	/**
	 * Header of the whole component
	 */
	readonly label: MessageDescriptor;
	readonly field: AssociatedField;
	readonly fieldErrors: ReadonlyArray<ValidationError>;
	readonly DefaultValueControlLeft?: ComponentType<DefaultValueControlComponentProps>;
	readonly DefaultValueControlRight?: ComponentType<DefaultValueControlComponentProps>;
	readonly OptionComponent: ComponentType<OptionProps>;
	readonly onUpdate: (field: AssociatedField) => void;
	readonly limits: Limits;
	tooltipDelay?: number;
	testId?: string;
	autofocus?: boolean;
};

const getTranslatedErrorFor = (
	option: OptionType,
	fieldErrors: ReadonlyArray<ValidationError>,
	intl: IntlShape,
): string | undefined => {
	const error: ValidationError | undefined = fieldErrors.find(
		(err) =>
			err.position === POSITION_FIELD_OPTIONS &&
			err.positionId != null &&
			err.positionId.optionOptimisticId === option.optimisticId,
	);
	if (!error) {
		return undefined;
	}
	return intl.formatMessage(error.messageDescriptor, error.values);
};
export const unknownToOptionType = (defaultValue: unknown): OptionType | null => {
	if (
		defaultValue == null ||
		typeof defaultValue !== 'object' ||
		!('id' in defaultValue) ||
		(defaultValue.id !== null && typeof defaultValue.id !== 'number')
	) {
		return null;
	}

	let optimisticId;
	if ('optimisticId' in defaultValue && typeof defaultValue.optimisticId === 'string') {
		optimisticId = defaultValue.optimisticId;
	} else if (defaultValue.id !== null) {
		optimisticId = defaultValue.id.toString();
	} else {
		// This scenario will never occur as whenever id is null optimisticId will exist. However we return null here to prevent flow errors.
		return null;
	}

	let externalUuid;
	if ('externalUuid' in defaultValue && typeof defaultValue.externalUuid === 'string') {
		externalUuid = toOptionUuid(defaultValue.externalUuid);
	}

	let value: string;
	if ('value' in defaultValue) {
		value = String(defaultValue.value);
	} else {
		value = '';
	}

	return {
		id: defaultValue.id,
		optimisticId,
		value,
		externalUuid,
	};
};

export const refineDefaultValue = (defaultValue: unknown): ReadonlyArray<OptionType> => {
	let maybeOptions: ReadonlyArray<OptionType | null>;
	/*
	 * This file handles both radios and checkboxes.
	 * Radios can only have one preselected option so the `defaultValue` will be always an object.
	 * Checkboxes can have multiple so the `defaultValue` is always an array (even when only one is preselected).
	 * Or null.
	 */
	if (Array.isArray(defaultValue)) {
		maybeOptions = defaultValue.map(unknownToOptionType);
	} else {
		maybeOptions = [unknownToOptionType(defaultValue)];
	}
	return maybeOptions.filter(Boolean);
};

export const isDefaultOption = (defaultValue: ReadonlyArray<OptionType>) => (option: OptionType) =>
	defaultValue.some((defaultOption: OptionType) =>
		defaultOption.id === null
			? defaultOption.optimisticId === option.optimisticId
			: defaultOption.id === option.id,
	);

export const isOptionsPerFieldLimitReached = (field: AssociatedField, limits: Limits): boolean => {
	const limit = limits.optionsPerField;
	const { options } = field;
	return limit !== null && limit > 0 && options !== undefined && options.length >= limit;
};

const getAddButtonTooltip = (
	field: AssociatedField,
	limits: Limits,
	intl: IntlShape,
): string | null => {
	if (!field.operations.canModifyOptions) {
		return intl.formatMessage(messages.addButtonDisabledNoModify);
	}
	if (isOptionsPerFieldLimitReached(field, limits)) {
		return intl.formatMessage(messages.addButtonDisabledMaxOptions, {
			maxOptions: limits.optionsPerField,
		});
	}
	return null;
};

function canAddNewOptions(field: AssociatedField, limits: Limits) {
	return field.operations.canModifyOptions && !isOptionsPerFieldLimitReached(field, limits);
}

export const FieldOptions = (props: Props) => {
	const optionsModified = (options: OptionType[]) => {
		const { field, onUpdate } = props;
		onUpdate({
			...field,
			options,
		});
	};

	const prevOptionsCount = usePrevious(props.field?.options?.length || 0);

	const onEditOption = (option: OptionType, newValue: string) => {
		const {
			field: { options = [] },
		} = props;
		const newOptions: OptionType[] = options.map((o) => {
			if (o === option) {
				return { ...o, value: newValue };
			}
			return o;
		});
		optionsModified(newOptions);
	};

	const onSaveColor = (option: OptionType, newColor: string) => {
		const {
			field: { options = [] },
		} = props;
		const newOptions: OptionType[] = options.map((o) => {
			if (o === option) {
				return { ...o, colour: newColor };
			}
			return o;
		});
		optionsModified(newOptions);
	};

	const onRemoveOption = (option: OptionType) => {
		const {
			field: { options = [] },
		} = props;
		if (options.length === 1) {
			// There must be at least one option so do not allow deleting the last one
			return;
		}

		optionsModified(options.filter((o) => o !== option));
	};

	const onAddOption = () => {
		const {
			field,
			field: { options = [] },
			limits,
		} = props;

		// Prevent adding a new option when the add button is disabled and the enter key is pressed
		if (!canAddNewOptions(field, limits)) {
			return;
		}

		const last = options[options.length - 1];
		// Do not add another empty option if there is one already
		if (last && last.value === '') {
			return;
		}
		const optionExternalUuid = toOptionUuid(uuid());
		optionsModified([
			...options,
			// Prefix optimisticId to avoid collision with options returned from the API
			{
				value: '',
				id: null,
				optimisticId: uniqueId(NEW_FIELD_OPTION_PREFIX),
				externalUuid: optionExternalUuid,
				...(field.type === SELECT_CF_TYPE &&
				expVal(
					'thor_colourful_single_select_milestone2_experiment',
					'isColorfulSingleSelectEnabled',
					false,
				)
					? { colour: getRandomColor() }
					: {}),
			},
		]);
	};

	const {
		field,
		fieldErrors,
		field: { options = [] },
		label,
		DefaultValueControlLeft,
		DefaultValueControlRight,
		OptionComponent,
		limits,
		onUpdate,
		autofocus = true,
	} = props;
	const isAddButtonDisabled = !canAddNewOptions(field, limits);

	const intl = useIntl();

	const preselectedOptions: ReadonlyArray<OptionType> = refineDefaultValue(field.defaultValue);

	// There must be at least 1 option
	const canDeleteOptions = options.length > 1;

	// Droppable only rerenders when drag-and-drop information changes.
	// Options are not drag and drop information, they come from us,
	// so we need to use a different function every time DropdownOptions renders,
	// to trigger a prop change (therefore rerender) of Droppable.
	return (
		<Box xcss={dropdownOptionsStyledStyles}>
			<Box xcss={headerStyles}>
				<Heading size="xxsmall" as="div">
					{intl.formatMessage(label)}
				</Heading>
			</Box>
			<Droppable droppableId={field.key} type={OPTION_DRAGGABLE_TYPE}>
				{({ innerRef, droppableProps, placeholder }: DroppableProvided) => (
					<List ref={innerRef} {...droppableProps}>
						{options.map((option, index) => {
							// only the last option should has autoFocus=true,
							// and if there is only 1 option we should disable autoFocus,
							// otherwise it will compete with field name input when we create a new field
							const isLastOption = index > 0 && index === options.length - 1;
							const isNewOptionCreated =
								prevOptionsCount != null ? options.length > prevOptionsCount : false;

							return (
								<OptionComponent
									key={option.optimisticId}
									placeholder={intl.formatMessage(messages.optionPlaceholder, {
										index: index + 1,
									})}
									option={option}
									field={field}
									autoFocus={
										!autofocus && fg('field-options-bounce-autofocus-fix')
											? isNewOptionCreated && isLastOption
											: isLastOption
									}
									onRemove={onRemoveOption}
									onEnterKey={onAddOption}
									onUpdate={onUpdate}
									onEdit={onEditOption}
									onSaveColor={onSaveColor}
									isDeletable={canDeleteOptions}
									isDefault={isDefaultOption(preselectedOptions)(option)}
									DefaultValueControlLeft={DefaultValueControlLeft}
									DefaultValueControlRight={DefaultValueControlRight}
									translatedError={getTranslatedErrorFor(option, fieldErrors, intl)}
									index={index}
								/>
							);
						})}
						{/* Render the droppable placeholder so content doesn't shift when dragging/dropping elements */}
						{placeholder}
					</List>
				)}
			</Droppable>

			<Tooltip content={getAddButtonTooltip(field, limits, intl)} delay={props?.tooltipDelay}>
				<DisabledButtonWithTooltip isDisabled={isAddButtonDisabled} data-testid={props?.testId}>
					<Button
						isDisabled={isAddButtonDisabled}
						appearance="subtle"
						// label is not needed on this icon because there is button text
						iconBefore={<EditorAddIcon label="" />}
						onClick={onAddOption}
					>
						{intl.formatMessage(messages.addNewOption)}
					</Button>
				</DisabledButtonWithTooltip>
			</Tooltip>
		</Box>
	);
};

const dropdownOptionsStyledStyles = xcss({
	paddingRight: 'space.500',
	paddingLeft: 'space.500',
	marginBottom: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ol({
	listStyleType: 'none',
	paddingLeft: token('space.0'),
	marginTop: token('space.0'),
	marginRight: token('space.0'),
	marginBottom: token('space.0'),
	marginLeft: token('space.0'),
});

const headerStyles = xcss({
	paddingBottom: 'space.050',
});
